import React, { useEffect, useState, useRef, useCallback } from 'react'
import { loadStripe } from '@stripe/stripe-js'

const API_BASE = 'https://backend.alephmedia.ro/api'

// Cache pentru instanța Stripe (shared între toate componentele)
let stripeInstanceCache = null
let stripeConfigCache = null
let initializationPromise = null // Promise pentru inițializare în curs

const ApplePayButton = ({ 
  amount,
  currency,
  label,
  onSuccess, 
  onError 
}) => {
  const [stripe, setStripe] = useState(null)
  const [paymentRequest, setPaymentRequest] = useState(null)
  const [applePayAvailable, setApplePayAvailable] = useState(false)
  const mountedRef = useRef(true)
  const paymentRequestRef = useRef(null)
  const initializedRef = useRef(false)
  
  // Memoizează callback-urile pentru a evita re-inițializări
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)
  
  useEffect(() => {
    onSuccessRef.current = onSuccess
    onErrorRef.current = onError
  }, [onSuccess, onError])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Funcție pentru crearea payment request-ului
  const createPaymentRequest = useCallback((stripeInstance, config) => {
    if (!stripeInstance || !config || !mountedRef.current) return

    try {
      // Dacă avem deja un payment request, îl distrugem
      if (paymentRequestRef.current) {
        paymentRequestRef.current.off('paymentmethod')
        paymentRequestRef.current = null
      }

      // Creează payment request
      const pr = stripeInstance.paymentRequest({
        country: config.country || 'RO',
        currency: currency || 'eur',
        total: {
          label: label || 'Abonament Aleph',
          amount: amount || 499,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })

      paymentRequestRef.current = pr

      // Verifică dacă Apple Pay e disponibil (doar o dată)
      pr.canMakePayment().then((canMakePayment) => {
        if (!mountedRef.current) return

        if (canMakePayment && canMakePayment.applePay) {
          setApplePayAvailable(true)
          setPaymentRequest(pr)

          // Event handler pentru plată
          pr.on('paymentmethod', async (ev) => {
            try {
              // Creează Payment Intent
              const intentRes = await fetch(`${API_BASE}/payment/apple-pay/create-intent`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  amount: amount,
                  currency: currency,
                  description: label,
                }),
              })

              const intentData = await intentRes.json()
              
              if (!intentData.success) {
                throw new Error(intentData.error || 'Eroare la creare payment intent')
              }

              // Confirmă plata
              const { error: confirmError, paymentIntent } = await stripeInstance.confirmCardPayment(
                intentData.data.client_secret,
                {
                  payment_method: ev.paymentMethod.id,
                },
                { handleActions: false }
              )

              if (confirmError) {
                ev.complete('fail')
                throw confirmError
              }

              ev.complete('success')

              if (onSuccessRef.current) {
                onSuccessRef.current(paymentIntent)
              }

            } catch (error) {
              console.error('Apple Pay payment error:', error)
              ev.complete('fail')
              if (onErrorRef.current) {
                onErrorRef.current(error.message || 'Eroare la procesarea plății')
              }
            }
          })
        } else {
          setApplePayAvailable(false)
        }
        initializedRef.current = true
      }).catch((error) => {
        console.error('canMakePayment error:', error)
        if (mountedRef.current) {
          setApplePayAvailable(false)
        }
        initializedRef.current = true
      })
    } catch (error) {
      console.error('createPaymentRequest error:', error)
      initializedRef.current = true
    }
  }, [amount, currency, label])

  useEffect(() => {
    // Dacă nu avem amount/currency/label, nu inițializăm
    if (!amount || !currency || !label) {
      return
    }

    // Dacă deja avem payment request și este valid, nu reinițializăm
    if (paymentRequestRef.current && paymentRequest && initializedRef.current) {
      return
    }

    // Dacă există deja o inițializare în curs, așteptăm
    if (initializationPromise) {
      initializationPromise.then(() => {
        if (mountedRef.current && stripeInstanceCache && stripeConfigCache) {
          createPaymentRequest(stripeInstanceCache, stripeConfigCache)
        }
      })
      return
    }

    // Dacă avem deja cache, folosim direct
    if (stripeInstanceCache && stripeConfigCache) {
      createPaymentRequest(stripeInstanceCache, stripeConfigCache)
      return
    }

    // Inițializăm Stripe
    initializationPromise = (async () => {
      try {
        // Încarcă config doar dacă nu avem cache
        let config = stripeConfigCache
        if (!config) {
          const configRes = await fetch(`${API_BASE}/payment/apple-pay/config`)
          config = await configRes.json()
          
          if (!config.publishableKey) {
            console.error('❌ Apple Pay config missing publishableKey:', config)
            initializationPromise = null
            return
          }
          stripeConfigCache = config
        }

        // Inițializare Stripe doar dacă nu avem cache
        let stripeInstance = stripeInstanceCache
        if (!stripeInstance) {
          stripeInstance = await loadStripe(config.publishableKey)
          stripeInstanceCache = stripeInstance
        }

        if (!mountedRef.current) {
          initializationPromise = null
          return
        }

        setStripe(stripeInstance)
        createPaymentRequest(stripeInstance, config)
      } catch (error) {
        console.error('Apple Pay initialization error:', error)
        if (onErrorRef.current) {
          onErrorRef.current(error.message)
        }
      } finally {
        initializationPromise = null
      }
    })()

  }, [amount, currency, label, createPaymentRequest, paymentRequest])

  useEffect(() => {
    if (!paymentRequest || !applePayAvailable || !stripe) {
      return
    }

    let prButton = null
    const buttonContainer = document.getElementById('apple-pay-button-container')
    
    if (!buttonContainer) {
      return
    }

    // Verificăm dacă există deja un buton montat
    if (buttonContainer.children.length > 0) {
      return
    }

    try {
      const elements = stripe.elements()
      prButton = elements.create('paymentRequestButton', {
        paymentRequest: paymentRequest,
        style: {
          paymentRequestButton: {
            type: 'default',
            theme: 'dark',
            height: '48px',
          },
        },
      })

      prButton.mount('#apple-pay-button-container')
    } catch (error) {
      console.error('Error mounting payment button:', error)
    }

    return () => {
      if (prButton) {
        try {
          prButton.destroy()
        } catch (error) {
          // Ignorăm erorile la destroy
        }
      }
    }
  }, [paymentRequest, applePayAvailable, stripe])

  if (!applePayAvailable) {
    return (
      <div style={{ 
        padding: '16px',
        textAlign: 'center',
        color: '#6b7280',
        fontSize: '14px'
      }}>
        Apple Pay nu este disponibil pe acest dispozitiv
      </div>
    )
  }

  return (
    <div 
      id="apple-pay-button-container" 
      style={{ 
        marginTop: '16px',
        marginBottom: '16px'
      }}
    />
  )
}

export default ApplePayButton
